export default function AgentsProgressGrid({ data, loading }) {
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-success'
      case 'started':
        return 'text-warning'
      case 'error':
        return 'text-error'
      default:
        return 'text-base-content/60'
    }
  }

  const formatAgentName = (name) => {
    // Handle specific agent name mappings (check both raw and formatted versions)
    const nameMapping = {
      'aa_sleep_medicine': 'American Academy of Sleep Medicine',
      'Aa Sleep Medicine': 'American Academy of Sleep Medicine',
      'nih': 'NIH',
      'Nih': 'NIH',
      'pubmed': 'PubMed',
      'Pubmed': 'PubMed',
      'alzheimers_association': "Alzheimer's Association",
      'Alzheimers Association': "Alzheimer's Association",
      'alzheimers_society': "Alzheimer's Society",
      'Alzheimers Society': "Alzheimer's Society"
    }
    
    // Check if there's a specific mapping for this name (case-insensitive)
    const lowerName = name.toLowerCase()
    for (const [key, value] of Object.entries(nameMapping)) {
      if (key.toLowerCase() === lowerName || key === name) {
        return value
      }
    }
    
    // Default formatting: replace underscores with spaces and capitalize words
    return name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return '✓'
      case 'started':
        return '⟳'
      case 'error':
        return '✗'
      default:
        return '○'
    }
  }

  // Convert agent progress data from websocket format to display format
  const agents = data ? Object.values(data).map(agent => ({
    name: agent.agent || 'Unknown',
    status: agent.status || 'unknown',
    docs_found: agent.docs_found || 0,
    sources: agent.sources || []
  })) : []

  const completedAgents = agents.filter(agent => agent.status === 'completed').length
  const runningAgents = agents.filter(agent => agent.status === 'started').length
  const totalDocs = agents.reduce((sum, agent) => sum + agent.docs_found, 0)

  return (
    <div className={`card bg-base-100 shadow-xl ${loading ? 'loading' : ''}`}>
      <div className="card-body">
        <h2 className="card-title">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23-.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" />
          </svg>
          Agent Progress
        </h2>
        
        {loading && !data ? (
          <div className="flex justify-center py-4">
            <span className="loading loading-spinner loading-md"></span>
          </div>
        ) : agents.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="table table-zebra">
              <thead>
                <tr>
                  <th className="w-1/4">Agent</th>
                  <th className="w-1/4">Status</th>
                  <th className="w-1/4">Documents</th>
                  <th className="w-1/4">Sources</th>
                </tr>
              </thead>
              <tbody>
                {agents.map((agent, index) => (
                  <tr key={index} className="h-12">
                    <td className="font-medium">
                      {formatAgentName(agent.name)}
                    </td>
                    <td>
                      <span className={`${getStatusColor(agent.status)} font-bold flex items-center gap-1 min-w-[80px]`}>
                        {agent.status === 'started' && (
                          <span className="loading loading-spinner loading-xs"></span>
                        )}
                        {getStatusIcon(agent.status)} {agent.status}
                      </span>
                    </td>
                    <td>
                      <span className="badge badge-primary">{agent.docs_found}</span>
                    </td>
                    <td>
                      <span className="text-sm text-base-content/60">
                        {agent.sources.length} sources
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            <div className="mt-4 text-sm text-base-content/60 flex justify-between">
              <span>Total agents: {agents.length}</span>
              <span>Completed: {completedAgents}</span>
              <span>Running: {runningAgents}</span>
              <span>Total docs: {totalDocs}</span>
            </div>
          </div>
        ) : (
          <div className="text-center py-4 text-base-content/60">
            Waiting for agents to start...
          </div>
        )}
      </div>
    </div>
  )
}